import { Button, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import { Dialog, DialogProps, DialogRef } from '@/components/common/Dialog';
import { useTranslationPrefix } from '@/translations/i18n';

export type DeleteRecordDialogRef = DialogRef;

export interface DeleteRecordDialogProps extends Pick<DialogProps, 'onClose'> {
  /**
   * The file name of the record to be deleted.
   */
  fileName: string;
  /**
   * Callback fired when the confirm button is clicked.
   */
  onConfirm: () => void;
  /**
   * Whether the dialog is loading or not. If `true`, the dialog will be disabled and the confirm button will be loading.
   */
  isLoading?: boolean;
}

export const DeleteRecordDialog = forwardRef<DeleteRecordDialogRef, DeleteRecordDialogProps>(
  ({ fileName, onConfirm, onClose, isLoading }, ref) => {
    const dialogRef = useRef<DialogRef>(null);
    const t = useTranslationPrefix('home.records.delete_dialog');

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => dialogRef.current!);

    return (
      <Dialog
        ref={dialogRef}
        containerProps={{ p: '25px' }}
        title={t('title')}
        actions={
          <>
            <Button
              size="small"
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={() => dialogRef.current?.close()}>
              {t('cancel')}
            </Button>
            <Button size="small" fullWidth loading={isLoading} onClick={onConfirm}>
              {t('confirm')}
            </Button>
          </>
        }
        onClose={onClose}
        disableClose={isLoading}>
        <Typography variant="body2" color="text.secondary">
          {t('description', { fileName })}
        </Typography>
      </Dialog>
    );
  },
);
